import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import ErrorIcon from "@mui/icons-material/Error";
import HelpIcon from "@mui/icons-material/Help";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import React, { FunctionComponent, ReactNode } from "react";
import Button from "../button/Button";
import { blue, red, yellow, green } from "designSystem/styles/color";
import { ButtonEnum } from "designSystem/types/button-types";
import FlexRowCenter from "../flex/FlexRowCenter";
import Text from "../text/Text";
import ModalDialog, { ModalDialogProps } from "./ModalDialog";

const PREFIX = "MessageDialog";

const classes = {
	root: `${PREFIX}root`,
	bottomContentPadding: `${PREFIX}bottomContentPadding`,
};

const StyledModalDialog = styled(ModalDialog)(({
	theme,
}) => ({
	[`& .${classes.root}`]: {
		"& > svg:first-of-type": {
			fontSize: 24,
			marginRight: theme.spacing(2),
		},
		"& > [role='progressbar']": {
			fontSize: 24,
			marginRight: theme.spacing(2),
		},
	},

	[`&.${classes.bottomContentPadding}`]: {
		paddingBottom: 40,
	},
}));

const colorMap = {
	error: red[500],
	help: blue[500],
	info: blue[500],
	warn: yellow[500],
	success: green[600],
};

const iconMap = {
	error: ErrorIcon,
	help: HelpIcon,
	info: InfoIcon,
	warn: WarningIcon,
	success: CheckCircleIcon,
};

export type MessageDialogCategory = "error" | "help" | "info" | "progress" | "success" | "warn";

export interface MessageDialogProps extends Omit<ModalDialogProps, "showDividers"> {
	/** localization resource for lone button if no buttons specified (defaults to OK) */
	buttonRes?: string;

	/** category of message which maps to default icon (default none) */
	category: MessageDialogCategory;

	/** child components of message dialog (leaving possibility for extra content like checkboxes, etc.) */
	children?: {
		/** actions for the message dialog */
		buttons?: ReactNode;

		/** custom icon for the message dialog */
		icon?: ReactNode;
	};

	/** message to display in dialog (if not specifying localization resource) */
	message?: string;

	/** localization resource for confirmation message */
	messageRes?: string;

	/** parameters for localization resource for confirmation message */
	messageResParams?: Array<number | string>;
}

const MessageDialog: FunctionComponent<MessageDialogProps> = (props) => {
	const {
		buttonRes,
		category,
		children,
		className,
		message,
		messageRes,
		messageResParams,
		...otherProps
	} = props;

	const IconComponent = iconMap[category];
	const isProgress = category && !IconComponent;
	let buttons = children?.buttons;
	let icon = children?.icon;

	let messageDialogClassName = className || "";

	if (!buttons) {
		if (isProgress) {
			messageDialogClassName = `${messageDialogClassName} ${classes.bottomContentPadding}`;
		} else {
			buttons = (
				<Button
					autoFocus
					design="primary"
					onClick={props.onClose}
					res={buttonRes || ButtonEnum.BUTTON_OK}
				/>
			);
		}
	}

	if (!icon && category) {
		if (IconComponent) {
			icon = <IconComponent style={{ color: colorMap[category] }} />;
		} else {
			icon = <CircularProgress size={20} />;
		}
	}

	const messageComponent = (
		<FlexRowCenter className={classes.root}>
			{icon}
			<Text
				res={messageRes}
				resParams={messageResParams}
				text={message}
			/>
		</FlexRowCenter>
	);

	return (
		<StyledModalDialog
			className={messageDialogClassName}
			{...otherProps}
		>
			{{
				main: messageComponent,
				buttons,
			}}
		</StyledModalDialog>
	);
};
export default MessageDialog;